<!-- Reusable Templates -->
<!-- Cell Content Template -->
<ng-template #cellContentTemplate let-item="item" let-col="col">
    <!-- Text type -->
    <span *ngIf="col.type === 'text' || !col.type" [class]="col.styleClass || ''">
        {{ getFieldValue(item, col.field) }}
    </span>

    <!-- Date type -->
    <span *ngIf="col.type === 'date'" [class]="col.styleClass || ''">
        {{ getFieldValue(item, col.field) | date:(col.dateFormat || 'dd/MM/yyyy') }}
    </span>

    <!-- Tag type -->
    <div *ngIf="col.type === 'tag'" [class]="col.styleClass || ''">
        <generic-p-tag [value]="getFieldValue(item, col.field)"
                       [type]="col.tagType || undefined"
                       [tagColor]="col.tagColor ? getFieldValue(item, col.tagColor) : undefined"
                       [tagTextColor]="col.tagTextColor ? getFieldValue(item, col.tagTextColor) : undefined">
        </generic-p-tag>
    </div>

    <!-- Link type -->
    <div *ngIf="col.type === 'link'" [class]="col.styleClass || ''">
        <a *ngIf="getFieldValue(item, col.field)"
           [href]="getFieldValue(item, col.field)"
           [target]="col.linkTarget || '_blank'"
           class="text-blue-600 hover:text-blue-800 underline">
            {{ col.linkText || getFieldValue(item, col.field) }}
        </a>
        <span *ngIf="!getFieldValue(item, col.field)">-</span>
    </div>

    <!-- Boolean type -->
    <span *ngIf="col.type === 'boolean'" [class]="col.styleClass || ''">
        <i *ngIf="getFieldValue(item, col.field)" class="pi pi-check !font-bold text-green-600"></i>
        <i *ngIf="!getFieldValue(item, col.field)" class="pi pi-times !font-bold text-red-600"></i>
    </span>

    <!-- Color type -->
    <div *ngIf="col.type === 'color'" [class]="'flex items-center gap-2 ' + (col.styleClass || '')">
        <div *ngIf="getFieldValue(item, col.field)"
             class="w-6 h-6 border border-gray-300 rounded"
             [style.background-color]="getFieldValue(item, col.field)"
             [pTooltip]="getFieldValue(item, col.field)">
        </div>
    </div>

    <!-- Custom type - template reference -->
    <ng-container *ngIf="col.type === 'custom'">
        <ng-container *ngIf="customColumnTemplate">
            <ng-container *ngTemplateOutlet="customColumnTemplate; context: { $implicit: item, field: col.field, column: col }">
            </ng-container>
        </ng-container>
        <!-- Fallback for custom types without template -->
        <span *ngIf="!customColumnTemplate" [class]="col.styleClass || ''">
            {{ getFieldValue(item, col.field) }}
        </span>
    </ng-container>
</ng-template>

<!-- Row Actions Template -->
<ng-template #rowActionsTemplate let-item="item">
    <div class="flex gap-2">
        <!-- Custom Actions via Content Projection -->
        <ng-container *ngIf="customActionsTemplate">
            <ng-container *ngTemplateOutlet="customActionsTemplate; context: { $implicit: item }">
            </ng-container>
        </ng-container>

        <!-- Row Actions from Config -->
        <button *ngFor="let action of getRowActions()"
                pButton
                [icon]="action.icon || (action.useDefaultStyle === 'edit' ? 'pi pi-pencil' : action.useDefaultStyle === 'delete' ? 'pi pi-trash' : '')"
                [class]="'p-button-text p-button-sm ' + (action.styleClass || (action.useDefaultStyle === 'delete' ? 'p-button-danger ' : ''))"
                [severity]="action.severity"
                [pTooltip]="action.tooltip || (action.useDefaultStyle === 'edit' ? 'Chỉnh sửa' : action.useDefaultStyle === 'delete' ? 'Xoá' : '')"
                [disabled]="action.disabled ? action.disabled(item) : false"
                tooltipPosition="top"
                (click)="executeAction(action, item, $event)">
        </button>
    </div>
</ng-template>

<!-- Empty Message Template -->
<ng-template #emptyMessageTemplate>
    <tr style="height:46px">
        <td [attr.colspan]="config.columns.length + (hasActions ? 1 : 0)"
            class="text-center p-4">
            Chưa có {{ config.entityName }} nào cả.
        </td>
    </tr>
</ng-template>

<!-- Title Row -->
<div *ngIf="config.features?.toolbar" class="flex-none mb-3">
    <div class="text-xl font-semibold">{{ config.title }}</div>
</div>

<!-- Toolbar -->
<p-toolbar *ngIf="config.features?.toolbar" class="flex-none" styleClass="mb-4 !border-none">
    <ng-template #start>
        <!-- Filter Toolbar in toolbar start -->
        <generic-filter-toolbar *ngIf="config.filterConfig"
            [config]="config.filterConfig"
            (filtersChanged)="onFiltersChanged($event)">
        </generic-filter-toolbar>
    </ng-template>
    <ng-template #end>
        <!-- Custom toolbar actions -->
        <ng-container *ngIf="toolbarActionsTemplate">
            <ng-container *ngTemplateOutlet="toolbarActionsTemplate"></ng-container>
        </ng-container>

        <!-- Toolbar actions from config -->
        <ng-container *ngFor="let action of getToolbarActions()">
            <p-button pRipple
                      styleClass="h-10"
                      [icon]="action.icon || (action.useDefaultStyle === 'add' ? 'pi pi-plus' : '')"
                      [label]="action.label || (action.useDefaultStyle === 'add' ? 'Thêm mới' : '')"
                      [severity]="action.severity"
                      [class]="action.styleClass"
                      [disabled]="action.disabled ? action.disabled(undefined) : false"
                      [pTooltip]="action.tooltip"
                      (onClick)="executeAction(action, undefined, $event)" />
        </ng-container>


    </ng-template>
</p-toolbar>

<!-- Table Container -->
<div class="flex flex-1 min-w-0 min-h-0 w-full overflow-auto">
    <div class="flex flex-col min-w-0 min-h-0 w-full overflow-auto">
        <!-- Unified Table -->
        <p-table #dataTable
                 class="flex flex-1 min-w-0 min-h-0 overflow-auto"
                 styleClass="flex-1 overflow-auto"
                 [rowHover]="true"
                 [rowTrackBy]="defaultTrackByFn"
                 [value]="data"
                 [totalRecords]="totalRecords"
                 [rows]="itemPerPage"
                 [loading]="loading"
                 [lazy]="config.useApiData !== false"
                 [selectionMode]="selectionMode"
                 [(selection)]="selection"
                 [metaKeySelection]="metaKeySelection"
                 [dataKey]="dataKey"
                 [scrollable]="config.features?.virtualScroll || true"
                 [scrollHeight]="config.features?.virtualScroll ? 'flex' : 'flex'"
                 [virtualScroll]="config.features?.virtualScroll || false"
                 [virtualScrollItemSize]="config.features?.virtualScroll ? virtualScrollItemSize : 0"
                 [virtualScrollOptions]="config.features?.virtualScroll ? virtualScrollOptions : {}"
                 [paginator]="!config.features?.virtualScroll"
                 [rowsPerPageOptions]="!config.features?.virtualScroll ? itemPerPageOptions : []"
                 [showCurrentPageReport]="!config.features?.virtualScroll"
                 [currentPageReportTemplate]="!config.features?.virtualScroll ? '{first} - {last} of {totalRecords} ' + config.entityName : ''"
                 (onRowSelect)="onTableRowSelect($event)"
                 (onRowUnselect)="onTableRowUnselect($event)"
                 (onLazyLoad)="onTableLazyLoad($event)">

            <!-- Header Template -->
            <ng-template pTemplate="header">
                <tr>
                    <th *ngFor="let col of config.columns"
                        [class]="col.width"
                        [pSortableColumn]="col.sortable ? col.field : undefined">
                        {{ col.header }}
                        <p-sortIcon *ngIf="col.sortable" [field]="col.field" />
                    </th>
                    <th *ngIf="hasActions" [class]="config.features?.virtualScroll ? 'min-w-[100px]' : undefined">Hành động</th>
                </tr>
            </ng-template>

            <!-- Body Template -->
            <ng-template pTemplate="body" let-item>
                <tr [style.height]="config.features?.virtualScroll ? '46px' : undefined" [pSelectableRow]="hasSelection ? item : undefined"
                    [ngClass]="{'disable-pointer': hasSelection == false}">
                    <td *ngFor="let col of config.columns" [class]="col.width">
                        <ng-container *ngTemplateOutlet="cellContentTemplate; context: { item: item, col: col }">
                        </ng-container>
                    </td>

                    <!-- Actions Column -->
                    <td *ngIf="hasActions" [class]="config.features?.virtualScroll ? 'min-w-[100px]' : undefined">
                        <ng-container *ngTemplateOutlet="rowActionsTemplate; context: { item: item }">
                        </ng-container>
                    </td>
                </tr>
            </ng-template>

            <!-- Empty Message -->
            <ng-template pTemplate="emptymessage">
                <ng-container *ngTemplateOutlet="emptyMessageTemplate">
                </ng-container>
            </ng-template>
        </p-table>
    </div>
</div>

<!-- Confirmation Dialog -->
<p-confirmDialog [style]="{width: '450px'}" dismissableMask="true" />

<!-- Content Projection for Dialogs and Additional Components -->
<ng-content></ng-content>